import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  X, FileSpreadsheet, Check, AlertTriangle, 
  RefreshCw, Upload, Users, UserPlus, Copy, ArrowLeft 
} from "lucide-react";

/**
 * ExcelPreviewDialog - Viser forhåndsvisning af Excel-data før import.
 * Brugeren kan vælge hvilke kolonner der mappes til hvilke felter.
 * For frivillige laves en dublet-analyse (gengangere) inden import bekræftes.
 */
export default function ExcelPreviewDialog({ 
  open, 
  onClose, 
  file, 
  fileType, 
  eventId, 
  token,
  onImportComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null); // dry-run resultat for frivillige

  useEffect(() => {
    if (open && file) {
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  const loadPreview = async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", fileType);
      
      const res = await axios.post(
        `${API}/events/${eventId}/preview-excel`,
        formData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setPreview(res.data);
      setMapping(res.data.suggested_mapping || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke læse Excel-fil");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const buildFormData = (dryRun) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);
    formData.append("mapping", JSON.stringify(mapping));
    if (dryRun) formData.append("dry_run", "true");
    return formData;
  };

  // Trin 1 (frivillige): analysér for gengangere uden at gemme
  const handleAnalyze = async () => {
    if (!mapping.email) {
      toast.error("Du skal vælge en kolonne til Email");
      return;
    }
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API}/events/${eventId}/import-with-mapping`,
        buildFormData(true),
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setAnalysis(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  // Trin 2 (frivillige) ELLER direkte (teams): udfør den faktiske import
  const handleImport = async () => {
    if (fileType === 'roles' && !mapping.name) {
      toast.error("Du skal vælge en kolonne til Teamnavn");
      return;
    }
    if (fileType === 'volunteers' && !mapping.email) {
      toast.error("Du skal vælge en kolonne til Email");
      return;
    }

    setImporting(true);
    try {
      const res = await axios.post(
        `${API}/events/${eventId}/import-with-mapping`,
        buildFormData(false),
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(res.data.message);
      onImportComplete?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved import");
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  const fieldLabels = {
    email: { label: "Email", required: true, description: "Email-adresse (påkrævet)" },
    first_name: { label: "Fornavn", required: false, description: "Fornavn (samles med efternavn)" },
    last_name: { label: "Efternavn", required: false, description: "Efternavn (samles med fornavn)" },
    name: { label: "Fuldt navn", required: fileType === 'roles', description: fileType === 'volunteers' ? "Brug hvis navnet IKKE er delt i for-/efternavn" : "Teamnavn (påkrævet)" },
    role: { label: "Rolle", required: false, description: "Tildelt rolle" },
    forening: { label: "Forening", required: false, description: "Forening/organisation" },
    hold: { label: "Hold", required: false, description: "Hold/team" },
    phone: { label: "Telefon", required: false, description: "Telefonnummer" },
    description: { label: "Beskrivelse", required: false, description: "Rollebeskrivelse" }
  };

  const availableFields = fileType === 'volunteers' 
    ? ['email', 'first_name', 'last_name', 'name', 'role', 'forening', 'hold', 'phone']
    : ['name', 'description'];

  // ===== ANALYSE-VISNING (kun frivillige) =====
  const renderAnalysis = () => {
    const s = analysis.summary || {};
    const newList = analysis.new_volunteers || [];
    const dupSystem = analysis.duplicates_in_system || [];
    const dupFile = analysis.duplicates_in_file || [];
    const totalDuplicates = dupSystem.length + dupFile.length;

    return (
      <>
        <div className="p-4 border-b border-border bg-muted/30" data-testid="import-analysis-summary">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-2 text-green-400">
                <UserPlus className="h-4 w-4" />
                <span className="text-2xl font-bold" data-testid="analysis-new-count">{s.new ?? newList.length}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">nye frivillige tilføjes</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-center gap-2 text-yellow-400">
                <Copy className="h-4 w-4" />
                <span className="text-2xl font-bold" data-testid="analysis-duplicate-count">{totalDuplicates}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">gengangere springes over</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-5">
          {/* Gengangere som springes over */}
          {totalDuplicates > 0 && (
            <div data-testid="duplicates-section">
              <div className="flex items-center gap-2 mb-2 text-yellow-400">
                <AlertTriangle className="h-4 w-4" />
                <h3 className="font-medium">Gengangere ({totalDuplicates}) — oprettes IKKE igen</h3>
              </div>
              <div className="border border-yellow-500/30 rounded-lg divide-y divide-border max-h-52 overflow-auto">
                {dupSystem.map((v, i) => (
                  <div key={`sys-${i}`} className="flex items-center justify-between p-2 text-sm">
                    <span className="font-medium">{v.name}</span>
                    <span className="text-muted-foreground">{v.email} · findes allerede</span>
                  </div>
                ))}
                {dupFile.map((v, i) => (
                  <div key={`file-${i}`} className="flex items-center justify-between p-2 text-sm">
                    <span className="font-medium">{v.name}</span>
                    <span className="text-muted-foreground">{v.email} · dublet i filen</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nye frivillige der tilføjes */}
          <div data-testid="new-section">
            <div className="flex items-center gap-2 mb-2 text-green-400">
              <UserPlus className="h-4 w-4" />
              <h3 className="font-medium">Nye frivillige ({newList.length}) — disse tilføjes</h3>
            </div>
            {newList.length > 0 ? (
              <div className="border border-green-500/30 rounded-lg divide-y divide-border max-h-52 overflow-auto">
                {newList.map((v, i) => (
                  <div key={i} className="flex items-center justify-between p-2 text-sm">
                    <span className="font-medium">{v.name}</span>
                    <span className="text-muted-foreground">{v.email}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground p-3 border border-border rounded-lg">
                Ingen nye frivillige — alle i filen findes allerede.
              </p>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between">
          <Button variant="outline" onClick={() => setAnalysis(null)} data-testid="analysis-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Tilbage
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={importing || newList.length === 0}
            data-testid="confirm-import-btn"
          >
            {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Bekræft – tilføj {newList.length} {newList.length === 1 ? 'frivillig' : 'frivillige'}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            {analysis ? <Users className="h-5 w-5 text-green-500" /> : <FileSpreadsheet className="h-5 w-5 text-green-500" />}
            <div>
              <h2 className="font-bold text-lg">
                {analysis ? 'Bekræft import: kontrollér gengangere' : `Forhåndsvisning: ${fileType === 'volunteers' ? 'Frivillige' : 'Teams'}`}
              </h2>
              {preview && (
                <p className="text-sm text-muted-foreground">
                  {preview.filename} • {preview.total_rows} rækker
                </p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-preview-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : analysis ? (
          renderAnalysis()
        ) : preview ? (
          <>
            {/* Kolonnemapping */}
            <div className="p-4 border-b border-border bg-muted/30">
              <h3 className="font-medium mb-3">Vælg kolonnemapping</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {availableFields.map(field => {
                  const info = fieldLabels[field];
                  return (
                    <div key={field} className="space-y-1">
                      <label className="text-sm font-medium flex items-center gap-1">
                        {info.label}
                        {info.required && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={mapping[field] || ''}
                        onChange={(e) => setMapping({...mapping, [field]: e.target.value || undefined})}
                        className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white text-sm"
                        data-testid={`mapping-select-${field}`}
                      >
                        <option value="">-- Vælg kolonne --</option>
                        {preview.headers.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">{info.description}</p>
                    </div>
                  );
                })}
              </div>
              
              {/* Automatisk mapping info */}
              {Object.keys(preview.suggested_mapping || {}).length > 0 && (
                <div className="mt-3 p-2 bg-green-500/10 rounded text-sm text-green-400 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Nogle kolonner blev automatisk genkendt baseret på navne
                </div>
              )}
            </div>

            {/* Preview tabel */}
            <div className="flex-1 overflow-auto p-4">
              <h3 className="font-medium mb-3">Forhåndsvisning (første 10 rækker)</h3>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {preview.headers.map(header => (
                        <th key={header} className="p-2 text-left font-medium border-b border-border">
                          <div className="flex items-center gap-1">
                            {header}
                            {Object.entries(mapping).some(([k, v]) => v === header) && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary">
                                → {Object.entries(mapping).find(([k, v]) => v === header)?.[0]}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview_rows.map((row, idx) => (
                      <tr key={idx} className="border-b border-border hover:bg-muted/30">
                        {preview.headers.map(header => (
                          <td key={header} className="p-2 truncate max-w-[200px]">
                            {row[header] || <span className="text-muted-foreground">-</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {preview.total_rows > preview.preview_rows.length && (
                <p className="text-sm text-muted-foreground mt-2">
                  ... og {preview.total_rows - preview.preview_rows.length} flere rækker
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {fileType === 'volunteers' ? (
                  mapping.email ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <Check className="h-4 w-4" /> Klar til analyse
                    </span>
                  ) : (
                    <span className="text-yellow-400 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" /> Vælg Email-kolonne
                    </span>
                  )
                ) : (
                  mapping.name ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <Check className="h-4 w-4" /> Klar til import
                    </span>
                  ) : (
                    <span className="text-yellow-400 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" /> Vælg Teamnavn-kolonne
                    </span>
                  )
                )}
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Annuller
                </Button>
                {fileType === 'volunteers' ? (
                  <Button 
                    onClick={handleAnalyze} 
                    disabled={analyzing || !mapping.email}
                    data-testid="analyze-import-btn"
                  >
                    {analyzing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Users className="h-4 w-4 mr-2" />
                    )}
                    Analysér gengangere
                  </Button>
                ) : (
                  <Button 
                    onClick={handleImport} 
                    disabled={importing || !mapping.name}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Importér {preview.total_rows} rækker
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
